import { Injectable } from '@angular/core';
import { PREVIEW } from '../preview/preview-mode';
import { BehaviorSubject } from 'rxjs';

import { AR, AR_RULES } from '../i18n/ar-dictionary';

export type Lang = 'en' | 'ar';
const LANG_KEY = 'ff_lang';

/** normalize curly quotes + collapse whitespace for robust matching */
function norm(s: string): string {
  return s
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

// pre-build normalized lookup once. MAP_CI is the case-insensitive fallback: API
// values arrive in their own casing ('preparing', 'apartment', 'home') while the
// catalogue is keyed on the UI's wording ('Preparing', 'Apartment', 'Home').
const MAP: Record<string, string> = {};
const MAP_CI: Record<string, string> = {};
Object.keys(AR).forEach((k) => {
  const n = norm(k);
  MAP[n] = AR[k];
  MAP_CI[n.toLowerCase()] = AR[k];
});

/** Handles the active language, text direction, and English→Arabic lookup. */
@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly _lang = new BehaviorSubject<Lang>(this.loadLang());
  readonly lang$ = this._lang.asObservable();

  get lang(): Lang { return this._lang.value; }
  get dir(): 'rtl' | 'ltr' { return this.lang === 'ar' ? 'rtl' : 'ltr'; }
  get isRtl(): boolean { return this.lang === 'ar'; }

  /**
   * Locale for Intl formatting (dates, times). Arabic uses Egyptian month and
   * weekday names but keeps Western digits (`-u-nu-latn`), matching the rest of
   * the UI, which renders every number in Latin digits.
   */
  get locale(): string { return this.lang === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB'; }

  setLang(lang: Lang): void {
    if (lang === this.lang) return;
    try { localStorage.setItem(LANG_KEY, JSON.stringify(lang)); } catch { /* ignore */ }
    // Full reload so every locale-dependent API response (brand, menu, cart, orders)
    // is refetched with the new Accept-Language header.
    window.location.reload();
  }

  toggle(): void { this.setLang(this.lang === 'en' ? 'ar' : 'en'); }

  /** Translate an English source string for the active language. */
  t(raw: string | null | undefined): string {
    if (raw == null) return '';
    if (this.lang !== 'ar') return raw;
    return this.translateAr(raw) ?? raw;
  }

  private translateAr(raw: string): string | null {
    const key = norm(raw);
    if (!key) return null;
    if (MAP[key]) return MAP[key];
    const ci = MAP_CI[key.toLowerCase()];
    if (ci) return ci;
    for (const [re, fn] of AR_RULES) {
      const m = key.match(re);
      if (m) return fn(m);
    }
    return null;
  }

  private loadLang(): Lang {
    if (PREVIEW.active && PREVIEW.lang) return PREVIEW.lang;
    try {
      const v = localStorage.getItem(LANG_KEY);
      return v ? (JSON.parse(v) as Lang) : 'en';
    } catch {
      return 'en';
    }
  }
}
